import boostPools from 'config/constants/boostPools'
import tokens from 'config/constants/tokens'
import { BoostPoolInfo } from 'config/constants/types'
import { createContext, useReducer } from 'react'
import { tryParseAmount } from 'state/swap/hooks'

export interface xVvsContextType {
  selectLockPeriod: (pid: number) => void
  setStep: (n: number) => void
  goNext: () => void
  goPrev: () => void
  onChange: (s: string) => void
  onReset: () => void
  state: xVvsReducerState
  error?: string
}
export const initState: xVvsReducerState = {
  selectedPool: undefined,
  step: 0,
  value: '',
}

export interface xVvsReducerState {
  selectedPool: BoostPoolInfo
  step: number
  value: string
}

const ON_SELECT_POOL = 'ON_SELECT_POOL'
const ON_SET_STEP = 'ON_SET_STEP'
const ON_PREV = 'ON_PREV'
const ON_NEXT = 'ON_NEXT'
const ON_CHANGE = 'ON_CHANGE'
const ON_RESET = 'ON_RESET'

interface OnSelectPoolAction {
  type: typeof ON_SELECT_POOL
  value: number
}

interface OnSetStepAction {
  type: typeof ON_SET_STEP
  value: number
}

interface OnPrevAction {
  type: typeof ON_PREV
}

interface OnNextAction {
  type: typeof ON_NEXT
}

interface OnChangeAction {
  type: typeof ON_CHANGE
  value: string
}

interface OnResetAction {
  type: typeof ON_RESET
}

type xVvsReducerAction =
  | OnSelectPoolAction
  | OnSetStepAction
  | OnPrevAction
  | OnNextAction
  | OnChangeAction
  | OnResetAction

function reducer(state: xVvsReducerState, action: xVvsReducerAction): xVvsReducerState {
  switch (action.type) {
    case ON_SELECT_POOL:
      return { ...state, selectedPool: boostPools.find((x) => x.pid === action.value) }
    case ON_SET_STEP:
      return { ...state, step: action.value }
    case ON_PREV:
      return { ...state, step: state.step - 1 }
    case ON_NEXT:
      return { ...state, step: state.step + 1 }
    case ON_CHANGE:
      return { ...state, value: action.value }
    case ON_RESET:
      return initState
    default:
      return { ...state }
  }
}

export const useXVvsContext = (): xVvsContextType => {
  const [state, dispatch] = useReducer(reducer, initState)

  const handleSelectPool = (pid: number) => {
    dispatch({ type: ON_SELECT_POOL, value: pid })
  }

  const goNext = () => dispatch({ type: ON_NEXT })
  const goPrev = () => dispatch({ type: ON_PREV })
  const handleReset = () => dispatch({ type: ON_RESET })
  const handleChange = (value: string) => dispatch({ type: ON_CHANGE, value })
  const handleSetStep = (value: number) => dispatch({ type: ON_SET_STEP, value })

  let error: string | undefined

  const parsedAmount = tryParseAmount(state.value, tokens.xvvs)
  if (!parsedAmount) {
    error = error ?? 'Enter an amount'
  }

  return {
    selectLockPeriod: handleSelectPool,
    setStep: handleSetStep,
    goNext,
    goPrev,
    onChange: handleChange,
    onReset: handleReset,
    state,
    error,
  }
}

const xVvsContext = createContext<xVvsContextType>({
  selectLockPeriod: (pid: number) => undefined,
  setStep: (n: number) => undefined,
  goNext: () => undefined,
  goPrev: () => undefined,
  onChange: (s: string) => undefined,
  onReset: () => undefined,
  state: initState,
})

export default xVvsContext
